import { useCallback, useMemo, useState, useEffect } from 'react';
import type { FolderId } from 'balus-common';
import { ViewModelEntity } from '@view-model/domain/view-model';
import { Folder } from '@workspace/view-model-folder/domain/Folder';
import { useParams } from 'react-router';
import type { RouteParams } from '@user/pages/RouteParams';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  controlAtom,
  setScrolledFolderAtom,
  setScrolledViewModelAtom,
  sidebarTreeContainerRefAtom,
} from './SidebarTreeStore/sidebarTreeControlState';
import { openFolderIdsAtom, toggleFolderOpenAtom } from './SidebarTreeStore/sidebarTreeOpenState';
import { viewModelsAtom, foldersAtom } from './SidebarTreeStore/sidebarTreeRecordsState';
import type { RootFolderTree } from '@workspace/view-model-folder/domain/FolderTree';

type Return = {
  isOpen: boolean;
  isSelected: boolean;
  firstLoaded: boolean;
  toggleFolder: () => void;
  sortedFolders: Folder[];
  sortedViewModels: ViewModelEntity[];
  scrolledViewModelId: string | null;
  scrolledFolderId: string | null;
  resetScrollFolder: () => void;
  resetScrollViewModel: () => void;
  sidebarTreeContainerRef: React.RefObject<HTMLDivElement | null> | null;
};

export const useSidebarFolder = (folderId: FolderId, rootFolderTree: RootFolderTree): Return => {
  const viewModels = useAtomValue(viewModelsAtom);
  const folders = useAtomValue(foldersAtom);
  const sidebarTreeContainerRef = useAtomValue(sidebarTreeContainerRefAtom);

  const openFolderIds = useAtomValue(openFolderIdsAtom);
  const toggleFolderOpen = useSetAtom(toggleFolderOpenAtom);
  const { scrolledViewModelId, scrolledFolderId } = useAtomValue(controlAtom);
  const setScrolledFolder = useSetAtom(setScrolledFolderAtom);
  const setScrolledViewModel = useSetAtom(setScrolledViewModelAtom);

  const { folderId: folderIdParam } = useParams<RouteParams>();

  const isSelected = useMemo(() => folderId === folderIdParam, [folderId, folderIdParam]);

  const [firstLoaded, setFirstLoaded] = useState(false);

  const isOpen = useMemo(() => !!openFolderIds[folderId], [openFolderIds, folderId]);

  // ワークスペース直下のフォルダ及びビューモデルが全て取得済みかをチェックする
  useEffect(() => {
    if (firstLoaded) return;

    const childrenFolderIds = rootFolderTree.getFolderIdsOf(folderId);
    const childrenViewModelIds = rootFolderTree.getViewModelIdsOf(folderId);
    const alreadyLoadedFolderIds = Object.keys(folders);
    const alreadyLoadedViewModelIds = Object.keys(viewModels);

    if (
      (childrenFolderIds.length <= 0 && childrenViewModelIds.length <= 0) ||
      (childrenFolderIds.every((id) => alreadyLoadedFolderIds.includes(id)) &&
        childrenViewModelIds.every((id) => alreadyLoadedViewModelIds.includes(id)))
    ) {
      setFirstLoaded(true);
    }
  }, [rootFolderTree, firstLoaded, folders, viewModels, folderId]);

  const toggleFolder = useCallback(() => {
    toggleFolderOpen(folderId);
  }, [folderId, toggleFolderOpen]);

  const sortedFolders = useMemo(() => {
    return rootFolderTree
      .getFolderIdsOf(folderId)
      .map((fid) => folders[fid])
      .filter((folder) => !!folder)
      .sort(Folder.compare);
  }, [rootFolderTree, folders, folderId]);

  const sortedViewModels = useMemo(() => {
    const targetViewModelIds = rootFolderTree.getViewModelIdsOf(folderId);

    return targetViewModelIds
      .map((viewModelId) => viewModels[viewModelId])
      .filter((viewModel) => !!viewModel)
      .sort(ViewModelEntity.compare);
  }, [rootFolderTree, viewModels, folderId]);

  const resetScrollFolder = useCallback(() => {
    setScrolledFolder('');
  }, [setScrolledFolder]);

  const resetScrollViewModel = useCallback(() => {
    setScrolledViewModel('');
  }, [setScrolledViewModel]);

  return {
    isOpen,
    isSelected,
    firstLoaded,
    toggleFolder,
    sortedFolders,
    sortedViewModels,
    scrolledFolderId,
    scrolledViewModelId,
    resetScrollFolder,
    resetScrollViewModel,
    sidebarTreeContainerRef,
  };
};
